import CustomCheckbox from "components/form/CustomCheckbox";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { sendSmsMessage } from "store/slices/sendSmsSlice";
import { getCustomersList } from "store/slices/customersSlice";
import { getSendSmsValidationSchema } from "utils/validations";
import { MultiSelect as PrimeMultiSelect } from "primereact/multiselect";
import PageHeader from "components/common/PageHeader";

const isArabicText = (value = "") => /[؀-ۿ]/.test(value);
const SMS_LIMIT_EN = 160;
const SMS_LIMIT_AR = 70;

const SendSmsMessage = () => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const dispatch = useDispatch();

  const { isLoading } = useSelector((state) => state.SendSms);
  const { customersList } = useSelector((state) => state.Customers);
  const [customerOptions, setCustomerOptions] = useState([]);

  const initialValues = {
    type: "all",
    customers: [],
    message: "",
  };

  useEffect(() => {
    dispatch(getCustomersList(""));
  }, [dispatch]);

  useEffect(() => {
    if (customersList?.length) {
      setCustomerOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));

        customersList.forEach(({ _id, name, phone }) => {
          map.set(_id, {
            name: `${phone} ${name || ""}`,
            value: _id,
          });
        });

        return Array.from(map.values());
      });
    }
  }, [customersList]);

  const handleSubmit = (values, { resetForm }) => {
    handleSubmitForm({
      dispatch,
      action: sendSmsMessage,
      payload: values,
      onSuccess: () => resetForm(),
    });
  };

  return (
    <div className="page-content">
      <PageHeader title={t("Send SMS")} />
      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getSendSmsValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const limit = isArabicText(values?.message)
              ? SMS_LIMIT_AR
              : SMS_LIMIT_EN;
            const remaining = limit - (values?.message?.length || 0);

            return (
              <Form>
                <Row>
                  <Col lg={6}>
                    <div className="form_field">
                      <label>
                        {t("Send to")}
                        <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-3">
                        <CustomCheckbox
                          label={t("All Customers")}
                          checked={values?.type === "all"}
                          onChange={() => setFieldValue("type", "all")}
                          infoText={t(
                            "Sends this SMS to every registered customer.",
                          )}
                        />
                        <CustomCheckbox
                          label={t("Specific Customers")}
                          checked={values?.type === "specific_customers"}
                          onChange={() =>
                            setFieldValue("type", "specific_customers")
                          }
                          infoText={t(
                            "Sends this SMS only to the customers you select.",
                          )}
                        />
                      </div>
                    </div>
                  </Col>

                  {values?.type === "specific_customers" && (
                    <Col lg={6}>
                      <div className="form_field">
                        <label>
                          {t("Customers")}
                          <span className="text-danger">*</span>
                        </label>
                        <PrimeMultiSelect
                          value={values?.customers || []}
                          options={customerOptions}
                          onChange={(e) => setFieldValue("customers", e.value)}
                          optionLabel="name"
                          optionValue="value"
                          placeholder={t("Select Customers")}
                          display="comma"
                          maxSelectedLabels={3}
                          selectedItemsLabel={t("{0} customers selected")}
                          filter={true}
                          onFilter={(e) =>
                            dispatch(getCustomersList(e.filter))
                          }
                          className="custom_multiselect sending_sms_multiselect"
                          panelClassName="sending_sms_multiselect_panel"
                        />
                        {errors?.customers && touched?.customers && (
                          <div className="text-danger">
                            {errors.customers}
                          </div>
                        )}
                      </div>
                    </Col>
                  )}

                  <Col lg={12}>
                    <div className="form_field">
                      <label htmlFor="message">
                        {t("Message")}
                        <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={limit}
                        value={values?.message}
                        onChange={(e) =>
                          setFieldValue("message", e.target.value)
                        }
                        placeholder={t("Enter SMS message")}
                        className={`form-control ${
                          errors?.message && touched?.message
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <div className="d-flex justify-content-between mt-1">
                        <small className="text-muted">
                          {t(
                            "1 SMS segment: 160 characters (English) / 70 characters (Arabic)",
                          )}
                        </small>
                        <small
                          className={remaining < 0 ? "text-danger" : "text-muted"}
                        >
                          {remaining}/{limit}
                        </small>
                      </div>
                      {errors?.message && touched?.message && (
                        <div className="text-danger">{errors.message}</div>
                      )}
                    </div>
                  </Col>

                  <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                    >
                      {t("Cancel")}
                    </button>

                    <button
                      type="submit"
                      className="main_btn"
                      disabled={isLoading}
                    >
                      {t("Send")}
                    </button>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default SendSmsMessage;
